import React, { useState } from "react";
import {
  FaPlusCircle,
  FaEdit,
  FaTrash,
  FaCalendarAlt,
  FaHeartbeat,
  FaWeight,
  FaThermometerHalf,
  FaTint,
  FaRuler,
} from "react-icons/fa";

const VitalsLogger = () => {
  const [vitals, setVitals] = useState([]);
  const [newVitals, setNewVitals] = useState({
    spo2: "",
    systolic: "",
    diastolic: "",
    sugar: "",
    pulse: "",
    temperature: "",
    weight: "",
    height: "",
  });
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const handleLogVitals = () => {
    if (
      newVitals.spo2 ||
      (newVitals.systolic && newVitals.diastolic) ||
      newVitals.sugar ||
      newVitals.pulse ||
      newVitals.temperature ||
      newVitals.weight ||
      newVitals.height
    ) {
      if (isEditing) {
        setVitals((prevVitals) =>
          prevVitals.map((vital) =>
            vital.id === editingId ? { ...vital, ...newVitals } : vital
          )
        );
        setIsEditing(false);
        setEditingId(null);
      } else {
        setVitals((prevVitals) => [
          {
            ...newVitals,
            id: Date.now(),
            timestamp: new Date().toLocaleString(),
          },
          ...prevVitals,
        ]);
      }
      setNewVitals({
        spo2: "",
        systolic: "",
        diastolic: "",
        sugar: "",
        pulse: "",
        temperature: "",
        weight: "",
        height: "",
      });
    }
  };

  const handleEdit = (id) => {
    const vitalToEdit = vitals.find((vital) => vital.id === id);
    setNewVitals({
      spo2: vitalToEdit.spo2 || "",
      systolic: vitalToEdit.systolic || "",
      diastolic: vitalToEdit.diastolic || "",
      sugar: vitalToEdit.sugar || "",
      pulse: vitalToEdit.pulse || "",
      temperature: vitalToEdit.temperature || "",
      weight: vitalToEdit.weight || "",
      height: vitalToEdit.height || "",
    });
    setIsEditing(true);
    setEditingId(id);
  };

  const handleDelete = (id) => {
    setVitals((prevVitals) => prevVitals.filter((vital) => vital.id !== id));
  };

  return (
    <div className="p-6 bg-white shadow-lg rounded-xl">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-semibold text-gray-800 flex items-center space-x-2">
          {/* <FaHeartbeat className="text-red-500" /> */}
          <span>Vitals Logger</span>
        </h3>
        <button
          onClick={handleLogVitals}
          className="flex items-center space-x-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 shadow-md"
        >
          <FaPlusCircle />
          <span>{isEditing ? "Update Vitals" : "Log Vitals"}</span>
        </button>
      </div>

      {/* Input Fields */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        {[
          { label: "SpO₂ (%)", value: "spo2", placeholder: "Enter SpO₂" },
          {
            label: "Systolic (mmHg)",
            value: "systolic",
            placeholder: "Enter Systolic",
          },
          {
            label: "Diastolic (mmHg)",
            value: "diastolic",
            placeholder: "Enter Diastolic",
          },
          {
            label: "Blood Sugar (mg/dL)",
            value: "sugar",
            placeholder: "Enter Blood Sugar",
          },
          { label: "Pulse (bpm)", value: "pulse", placeholder: "Enter Pulse" },
          {
            label: "Temperature (°C)",
            value: "temperature",
            placeholder: "Enter Temperature",
          },
          {
            label: "Weight (kg)",
            value: "weight",
            placeholder: "Enter Weight",
          },
          {
            label: "Height (cm)",
            value: "height",
            placeholder: "Enter Height",
          },
        ].map((field) => (
          <div key={field.value}>
            <label className="block text-sm font-medium text-gray-700">
              {field.label}
            </label>
            <input
              type="number"
              value={newVitals[field.value]}
              onChange={(e) =>
                setNewVitals({ ...newVitals, [field.value]: e.target.value })
              }
              placeholder={field.placeholder}
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
            />
          </div>
        ))}
      </div>

      {/* Logged Vitals */}
      <ul className="space-y-6">
        {vitals.length === 0 ? (
          <p className="text-gray-500 text-center">No vitals logged yet.</p>
        ) : (
          vitals.map((vital) => (
            <li
              key={vital.id}
              className="p-6 border border-gray-200 rounded-lg bg-gray-50 shadow-md hover:shadow-lg transition-shadow"
            >
              {/* Header: Timestamp and Actions */}
              <div className="flex justify-between items-center mb-4">
                <div className="text-sm text-gray-500 flex items-center space-x-2">
                  <FaCalendarAlt />
                  <span>{vital.timestamp}</span>
                </div>
                <div className="flex space-x-3">
                  <button
                    onClick={() => handleEdit(vital.id)}
                    className="px-3 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 flex items-center space-x-2"
                  >
                    <FaEdit />
                    <span>Edit</span>
                  </button>
                  <button
                    onClick={() => handleDelete(vital.id)}
                    className="px-3 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 flex items-center space-x-2"
                  >
                    <FaTrash />
                    <span>Delete</span>
                  </button>
                </div>
              </div>

              {/* Grid Layout for Vitals */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {vital.spo2 && (
                  <div className="flex items-center space-x-2 text-gray-800">
                    <FaTint className="text-blue-500" />
                    <p>
                      <strong>SpO₂:</strong> {vital.spo2}%
                    </p>
                  </div>
                )}
                {(vital.systolic || vital.diastolic) && (
                  <div className="flex items-center space-x-2 text-gray-800">
                    <FaHeartbeat className="text-red-500" />
                    <p>
                      <strong>Blood Pressure:</strong> {vital.systolic}/
                      {vital.diastolic} mmHg
                    </p>
                  </div>
                )}
                {vital.sugar && (
                  <div className="flex items-center space-x-2 text-gray-800">
                    <FaTint className="text-yellow-500" />
                    <p>
                      <strong>Blood Sugar:</strong> {vital.sugar} mg/dL
                    </p>
                  </div>
                )}
                {vital.pulse && (
                  <div className="flex items-center space-x-2 text-gray-800">
                    <FaHeartbeat className="text-green-500" />
                    <p>
                      <strong>Pulse:</strong> {vital.pulse} bpm
                    </p>
                  </div>
                )}
                {vital.temperature && (
                  <div className="flex items-center space-x-2 text-gray-800">
                    <FaThermometerHalf className="text-red-500" />
                    <p>
                      <strong>Temperature:</strong> {vital.temperature}°C
                    </p>
                  </div>
                )}
                {vital.weight && (
                  <div className="flex items-center space-x-2 text-gray-800">
                    <FaWeight className="text-blue-500" />
                    <p>
                      <strong>Weight:</strong> {vital.weight} kg
                    </p>
                  </div>
                )}
                {vital.height && (
                  <div className="flex items-center space-x-2 text-gray-800">
                    <FaRuler className="text-green-500" />
                    <p>
                      <strong>Height:</strong> {vital.height} cm
                    </p>
                  </div>
                )}
              </div>
            </li>
          ))
        )}
      </ul>
    </div>
  );
};

export default VitalsLogger;
